// ═══════════════════════════════════════════════════════════════════════════
// 📦 FirestoreManager: Camada centralizada de acesso ao Firestore
// ═══════════════════════════════════════════════════════════════════════════
//
// Responsabilidades:
// - Conectar ao Firestore
// - Executar todas as operações (getDocs, getDoc, addDoc, etc)
// - Cache em memória com TTL por coleção
// - Logging de operações
// - Estatísticas de hit/miss
// - Invalidação de cache
// - Base para futuras otimizações

import {
  getFirestore,
  collection,
  query,
  getDocs,
  getDoc,
  getCountFromServer,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  runTransaction,
  onSnapshot,
  serverTimestamp,
  increment,
  where,
  orderBy,
  limit,
  startAfter,
} from 'firebase/firestore';

/**
 * Configuração de TTL por coleção (em milissegundos)
 * 5 min = 5 * 60 * 1000 = 300000
 */
const COLLECTION_TTL_CONFIG = {
  rits: 5 * 60 * 1000,              // 5 minutos
  uas_ordens: 5 * 60 * 1000,        // 5 minutos
  uas_areas: 5 * 60 * 1000,         // 5 minutos
  users: 30 * 60 * 1000,            // 30 minutos
  counters: 5 * 60 * 1000,          // 5 minutos (aumentado: evita quota 429)
  ritCoords: 10 * 60 * 1000,        // 10 minutos
  user_analytics: 0,                // Sem cache (write-heavy)
  consultas_publicas_log: 0,        // Sem cache
  ips_bloqueados: 1 * 60 * 1000,    // 1 minuto
  avisos: 10 * 60 * 1000,           // 10 minutos
  videos: 30 * 60 * 1000,           // 30 minutos
  duvidas: 10 * 60 * 1000,          // 10 minutos
  senhasSistemas: 30 * 60 * 1000,   // 30 minutos
  treinamentos: 30 * 60 * 1000,     // 30 minutos
  certificados: 10 * 60 * 1000,     // 10 minutos
};

/**
 * FirestoreManager - Gerenciador centralizado de Firestore
 */
class FirestoreManager {
  constructor(firebaseApp) {
    this.db = getFirestore(firebaseApp);
    this.cache = new Map();                // Cache em memória: Map<cacheKey, {data, timestamp}>
    this.listeners = new Map();            // Listeners ativos: Map<listenerId, unsubscribe>
    this.inFlightRequests = new Map();     // Promises em voo: Map<cacheKey, Promise>
    this.transactionQueues = new Map();    // Fila de transações por chave: Map<queueKey, Promise>
    this.stats = {
      hits: 0,
      misses: 0,
      observedDocs: 0,
      queries: 0,
      writes: 0,
      errors: 0,
      dedupedRequests: 0,                  // Requisições reutilizadas via dedup
      swrInstantReturns: 0,                // Retornos imediatos de cache (stale-while-revalidate)
      swrRevalidations: 0,                 // Revalidações em segundo plano executadas
      swrDataChanged: 0,                   // Revalidações que detectaram alteração nos dados
      swrDataUnchanged: 0,                 // Revalidações sem alteração nos dados
    };
    this.operationHistory = [];            // Histórico detalhado de operações
    this.maxHistorySize = 500;             // Máximo de operações a manter
    this.logger = this.createLogger();
    // Persistência cross-sessão de métricas (opt-in via startAutoPersist).
    // Estado guardado aqui para o startAutoPersist ser idempotente.
    this._persistInterval = null;
    this._persistUnloadHandler = null;
    this._sessionId = null; // gerado no 1º startAutoPersist
    this._persistWindowStart = null;
  }

  /**
   * Logger simples com timestamp e níveis
   */
  createLogger() {
    return {
      log: (level, message, data = {}) => {
        const timestamp = new Date().toISOString();
        const prefix = `[${timestamp}] [FirestoreManager] [${level}]`;
        console.log(`${prefix} ${message}`, data);
      },
      debug: (message, data) => this.logger.log('DEBUG', message, data),
      info: (message, data) => this.logger.log('INFO', message, data),
      warn: (message, data) => this.logger.log('WARN', message, data),
      error: (message, data) => this.logger.log('ERROR', message, data),
    };
  }

  /**
   * Extrai o arquivo/linha de origem da chamada usando stack trace.
   * A regex antiga (/\(.*\/(.*):(\d+):(\d+)\)/) só casava em build de
   * desenvolvimento (formato "at fn (arquivo.js:12:3)"). Em produção o
   * bundler minifica e o stack vira algo como
   *   "at t.n (main.abc123.js:1:12345)"
   * ou "at http://.../main.abc123.js:1:12345" (sem parênteses).
   * As duas tentativas abaixo cobrem os dois formatos + preservam o nome
   * da função (t.n) quando disponível — que ajuda a rastrear a origem em
   * builds minificados mesmo com nomes de arquivos/linhas embaralhados.
   */
  getCallOrigin() {
    try {
      const stack = (new Error().stack || '').split('\n');
      for (let i = 3; i < Math.min(12, stack.length); i++) {
        const line = stack[i];
        if (!line || line.includes('FirestoreManager') || line.includes('executeWithDedup')) continue;
        // Tenta capturar o nome da função (útil em produção quando o nome
        // do arquivo é opaco tipo "main.js:1:12345")
        const fnMatch = line.match(/at\s+([\w$.<>[\] ]+?)\s+\(/);
        // Tenta capturar arquivo:linha (com ou sem parênteses)
        const fileMatch = line.match(/([\w.-]+\.js):(\d+):\d+/);
        if (fileMatch) {
          const file = fileMatch[1];
          const linha = fileMatch[2];
          const fn = fnMatch ? fnMatch[1] : '';
          return fn ? `${file}:${linha} (${fn})` : `${file}:${linha}`;
        }
      }
    } catch {
      // Ignorar erros ao extrair stack
    }
    return 'unknown';
  }

  /**
   * Registra uma operação no histórico com detalhes
   */
  trackOperation(operation) {
    if (!operation.error && !operation.cacheHit && Number.isFinite(operation.docCount) && operation.operation !== "getCount") {
      this.stats.observedDocs += Math.max(0, operation.docCount);
    }
    this.operationHistory.push({
      timestamp: new Date().toISOString(),
      ...operation,
    });

    // Limitar tamanho do histórico
    if (this.operationHistory.length > this.maxHistorySize) {
      this.operationHistory.shift();
    }
  }

  /**
   * Calcula quantas leituras foram economizadas até agora
   */
  calculateSavedReads() {
    // Deduplicação economiza 1 read por requisição reutilizada
    const dedupSavings = this.stats.dedupedRequests;

    // Cache hits economizam 1 read por hit (não vai para Firestore)
    const cacheSavings = this.stats.hits;

    return {
      dedupSavings,
      cacheSavings,
      totalSavings: dedupSavings + cacheSavings,
    };
  }

  /**
   * Gera chave de cache determinística baseada em coleção + operação + filtros
   * Exemplo: "rits:getDocs:3c2a1b9f"
   */
  getCacheKey(collectionName, operation = 'getDocs', params = {}) {
    // Criar hash simples dos parâmetros para chave mais concisa
    const paramString = JSON.stringify(params);
    const hash = this.simpleHash(paramString);
    return `${collectionName}:${operation}:${hash}`;
  }

  /**
   * Cria hash simples de uma string
   */
  simpleHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Converter para 32-bit
    }
    return Math.abs(hash).toString(16);
  }

  /**
   * Deduplicação de requisições
   * Se uma requisição idêntica já está em voo, reutiliza a Promise
   * Caso contrário, cria nova Promise e rastreia
   */
  async executeWithDedup(cacheKey, executeFn) {
    // Se já existe uma requisição em voo, retornar a Promise existente
    if (this.inFlightRequests.has(cacheKey)) {
      this.stats.dedupedRequests++;
      this.logger.debug(`Request deduped (reusing in-flight promise)`, { cacheKey });
      return this.inFlightRequests.get(cacheKey);
    }

    // Criar Promise para a requisição
    const promise = executeFn()
      .finally(() => {
        // Remover do rastreamento quando completar (sucesso ou erro)
        this.inFlightRequests.delete(cacheKey);
      });

    // Rastrear a Promise em voo
    this.inFlightRequests.set(cacheKey, promise);

    return promise;
  }

  /**
   * Verifica se cache está válido (não expirou)
   */
  isCacheValid(cacheKey) {
    const entry = this.cache.get(cacheKey);
    if (!entry) {
      this.stats.misses++;
      return false;
    }

    const collectionName = cacheKey.split(':')[0];
    const ttl = COLLECTION_TTL_CONFIG[collectionName] || 5 * 60 * 1000;

    // Se TTL é 0, cache nunca é válido
    if (ttl === 0) {
      this.cache.delete(cacheKey);
      this.stats.misses++;
      return false;
    }

    const ageMs = Date.now() - entry.timestamp;
    const isValid = ageMs < ttl;

    if (isValid) {
      this.stats.hits++;
      this.logger.debug(`Cache HIT (age: ${ageMs}ms, ttl: ${ttl}ms)`, { cacheKey });
    } else {
      this.cache.delete(cacheKey);
      this.stats.misses++;
      this.logger.debug(`Cache EXPIRED (age: ${ageMs}ms, ttl: ${ttl}ms)`, { cacheKey });
    }

    return isValid;
  }

  /**
   * Adiciona dados ao cache com verificação de TTL
   */
  setCacheEntry(cacheKey, data) {
    const collectionName = cacheKey.split(':')[0];
    const ttl = COLLECTION_TTL_CONFIG[collectionName] || 5 * 60 * 1000;

    // Não cachear se TTL é 0
    if (ttl === 0) {
      this.logger.debug(`Cache SKIPPED (TTL=0)`, { cacheKey });
      return;
    }

    const entry = {
      data,
      timestamp: Date.now(),
      size: JSON.stringify(data).length, // Tamanho em bytes
      ttl,
    };

    this.cache.set(cacheKey, entry);
    this.logger.debug(`Cache SET (size: ${entry.size}B, ttl: ${ttl}ms)`, { cacheKey });
  }

  /**
   * Limpa automaticamente entradas expiradas do cache
   * Deve ser chamado periodicamente ou quando cache fica muito grande
   */
  cleanExpiredCaches() {
    const before = this.cache.size;
    let deleted = 0;

    for (const [cacheKey, entry] of this.cache) {
      const collectionName = cacheKey.split(':')[0];
      const ttl = COLLECTION_TTL_CONFIG[collectionName] || 5 * 60 * 1000;

      if (ttl === 0) {
        // Nunca cachear, mas remover se estiver lá
        this.cache.delete(cacheKey);
        deleted++;
        continue;
      }

      const ageMs = Date.now() - entry.timestamp;
      if (ageMs >= ttl) {
        this.cache.delete(cacheKey);
        deleted++;
      }
    }

    this.logger.info(`Cleaned expired caches`, {
      before,
      deleted,
      after: this.cache.size,
    });

    return { before, deleted, after: this.cache.size };
  }

  /**
   * Invalida cache de uma coleção específica ou todos os caches
   */
  invalidateCache(collectionName = null) {
    if (!collectionName) {
      const deletedCount = this.cache.size;
      this.cache.clear();
      this.logger.warn('Invalidating ALL cache', { deletedCount });
      return { deleted: deletedCount, remaining: 0 };
    }

    const keysToDelete = [];
    for (const [key] of this.cache) {
      if (key.startsWith(`${collectionName}:`)) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach(key => this.cache.delete(key));

    this.logger.info(`Invalidated cache for collection: ${collectionName}`, {
      keysDeleted: keysToDelete.length,
      remaining: this.cache.size,
    });

    return {
      deleted: keysToDelete.length,
      remaining: this.cache.size,
      keys: keysToDelete,
    };
  }

  /**
   * Executa query e retorna múltiplos documentos
   * Usa deduplicação automática para requisições idênticas simultâneas
   */
  async getDocs(collectionName, queryConstraints = []) {
    const startTime = Date.now();
    const constraintsHash = this.simpleHash(JSON.stringify(queryConstraints));
    const cacheKey = this.getCacheKey(collectionName, 'getDocs', { constraintsHash });
    const origin = this.getCallOrigin();

    // Verificar cache primeiro (rápido)
    if (this.isCacheValid(cacheKey)) {
      const cachedData = this.cache.get(cacheKey).data;
      const duration = Date.now() - startTime;

      this.trackOperation({
        operation: 'getDocs',
        collection: collectionName,
        constraintCount: queryConstraints.length,
        origin,
        duration,
        docCount: cachedData.length,
        cacheHit: true,
        cacheMiss: false,
        deduped: false,
        savedReads: 1,
      });

      return cachedData;
    }

    // Executar com deduplicação de requisições simultâneas
    return this.executeWithDedup(cacheKey, async () => {
      try {
        this.stats.queries++;
        const startQueryTime = Date.now();

        this.logger.debug(`Executing getDocs (cache miss)`, {
          collection: collectionName,
          constraintCount: queryConstraints.length,
        });

        const q = query(collection(this.db, collectionName), ...queryConstraints);
        const snapshot = await getDocs(q);

        const data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        const queryDuration = Date.now() - startQueryTime;

        // Armazenar em cache
        this.setCacheEntry(cacheKey, data);

        const totalDuration = Date.now() - startTime;

        this.logger.info(`getDocs completed`, {
          collection: collectionName,
          docsCount: data.length,
          duration: queryDuration,
          cached: true,
        });

        this.trackOperation({
          operation: 'getDocs',
          collection: collectionName,
          constraintCount: queryConstraints.length,
          origin,
          duration: totalDuration,
          queryDuration,
          docCount: data.length,
          cacheHit: false,
          cacheMiss: true,
          deduped: false,
          savedReads: 0,
        });

        return data;
      } catch (error) {
        this.stats.errors++;
        const duration = Date.now() - startTime;

        this.logger.error(`getDocs failed for ${collectionName}`, { error: error.message });

        this.trackOperation({
          operation: 'getDocs',
          collection: collectionName,
          origin,
          duration,
          cacheHit: false,
          cacheMiss: true,
          error: error.message,
        });

        throw error;
      }
    });
  }

  /**
   * Contagem agregada (getCountFromServer) — muito mais barata que getDocs
   * quando só se precisa do número de documentos (ex.: "quantos RITs este
   * usuário já elaborou"), pois não trafega o conteúdo de cada documento,
   * apenas a contagem. Usa o mesmo cache/dedupe de getDocs.
   */
  async getCount(collectionName, queryConstraints = []) {
    const startTime = Date.now();
    const constraintsHash = this.simpleHash(JSON.stringify(queryConstraints));
    const cacheKey = this.getCacheKey(collectionName, 'getCount', { constraintsHash });
    const origin = this.getCallOrigin();

    if (this.isCacheValid(cacheKey)) {
      const cachedData = this.cache.get(cacheKey).data;
      this.trackOperation({
        operation: 'getCount',
        collection: collectionName,
        constraintCount: queryConstraints.length,
        origin,
        duration: Date.now() - startTime,
        cacheHit: true,
        cacheMiss: false,
        deduped: false,
        savedReads: 1,
      });
      return cachedData;
    }

    return this.executeWithDedup(cacheKey, async () => {
      try {
        this.stats.queries++;
        const q = query(collection(this.db, collectionName), ...queryConstraints);
        const snapshot = await getCountFromServer(q);
        const count = snapshot.data().count;

        this.setCacheEntry(cacheKey, count);

        this.logger.info(`getCount completed`, { collection: collectionName, count });

        this.trackOperation({
          operation: 'getCount',
          collection: collectionName,
          constraintCount: queryConstraints.length,
          origin,
          duration: Date.now() - startTime,
          cacheHit: false,
          cacheMiss: true,
          deduped: false,
          savedReads: 0,
        });

        return count;
      } catch (error) {
        this.stats.errors++;
        this.logger.error(`getCount failed for ${collectionName}`, { error: error.message });
        throw error;
      }
    });
  }

  /**
   * Executa query e retorna um único documento
   * Usa deduplicação automática para requisições idênticas simultâneas
   */
  async getDoc(collectionName, documentId) {
    const startTime = Date.now();
    const cacheKey = this.getCacheKey(collectionName, 'getDoc', { id: documentId });
    const origin = this.getCallOrigin();

    // Verificar cache primeiro (rápido)
    if (this.isCacheValid(cacheKey)) {
      const cachedData = this.cache.get(cacheKey).data;
      const duration = Date.now() - startTime;

      this.trackOperation({
        operation: 'getDoc',
        collection: collectionName,
        documentId,
        origin,
        duration,
        exists: !!cachedData,
        cacheHit: true,
        cacheMiss: false,
        deduped: false,
        savedReads: 1,
      });

      return cachedData;
    }

    // Executar com deduplicação de requisições simultâneas
    return this.executeWithDedup(cacheKey, async () => {
      try {
        this.stats.queries++;
        const startQueryTime = Date.now();

        this.logger.debug(`Executing getDoc (cache miss)`, {
          collection: collectionName,
          id: documentId,
        });

        const snapshot = await getDoc(doc(this.db, collectionName, documentId));

        const data = snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;

        const queryDuration = Date.now() - startQueryTime;

        // Armazenar em cache (inclusive null)
        this.setCacheEntry(cacheKey, data);

        const totalDuration = Date.now() - startTime;

        this.logger.info(`getDoc completed`, {
          collection: collectionName,
          id: documentId,
          exists: !!data,
          duration: queryDuration,
          cached: true,
        });

        this.trackOperation({
          operation: 'getDoc',
          collection: collectionName,
          documentId,
          origin,
          duration: totalDuration,
          queryDuration,
          exists: !!data,
          docCount: data ? 1 : 0,
          cacheHit: false,
          cacheMiss: true,
          deduped: false,
          savedReads: 0,
        });

        return data;
      } catch (error) {
        this.stats.errors++;
        const duration = Date.now() - startTime;

        this.logger.error(`getDoc failed for ${collectionName}/${documentId}`, { error: error.message });

        this.trackOperation({
          operation: 'getDoc',
          collection: collectionName,
          documentId,
          origin,
          duration,
          cacheHit: false,
          cacheMiss: true,
          error: error.message,
        });

        throw error;
      }
    });
  }

  /**
   * ═══════════════════════════════════════════════════════════════════════
   * Estratégia Stale-While-Revalidate (SWR)
   * ═══════════════════════════════════════════════════════════════════════
   *
   * Fluxo:
   * 1. Verifica se existe cache para a consulta (mesmo que o TTL já tenha
   *    vencido — dado "stale" ainda é servido, nunca é descartado só por isso)
   * 2. Se existir, retorna IMEDIATAMENTE os dados em cache. O usuário nunca
   *    aguarda o Firestore quando há cache disponível.
   * 3. Dispara uma consulta ao Firestore em segundo plano (revalidação),
   *    sem bloquear o retorno acima
   * 4. Se os dados retornados forem diferentes dos dados em cache, atualiza
   *    o cache e invoca onRevalidate(freshData) para a interface se atualizar
   *
   * Quando não há cache algum (primeiro acesso), não há dado para exibir de
   * imediato — a Promise aguarda a primeira consulta normalmente.
   */

  /**
   * Versão SWR de getDocs
   * @param collectionName - nome da coleção
   * @param queryConstraints - array de constraints (where, orderBy, limit, etc)
   * @param onRevalidate - callback(freshData) chamado SOMENTE quando os dados mudarem
   * @returns Promise<data> - resolve imediatamente com o cache, se existir
   */
  async getDocsSWR(collectionName, queryConstraints = [], onRevalidate = null) {
    const startTime = Date.now();
    const constraintsHash = this.simpleHash(JSON.stringify(queryConstraints));
    const cacheKey = this.getCacheKey(collectionName, 'getDocs', { constraintsHash });
    const origin = this.getCallOrigin();

    const revalidate = (isBackground) => this.executeWithDedup(cacheKey, async () => {
      try {
        this.stats.queries++;

        const q = query(collection(this.db, collectionName), ...queryConstraints);
        const snapshot = await getDocs(q);
        const freshData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const previousEntry = this.cache.get(cacheKey);
        const hasChanged = JSON.stringify(freshData) !== JSON.stringify(previousEntry ? previousEntry.data : undefined);

        this.setCacheEntry(cacheKey, freshData);

        // Notificar a interface SOMENTE quando é uma revalidação em segundo
        // plano (passo 3-4). No fetch inicial (sem cache), o próprio valor de
        // retorno da Promise já entrega o dado ao chamador.
        if (isBackground) {
          this.stats.swrRevalidations++;

          if (hasChanged) {
            this.stats.swrDataChanged++;
            this.logger.info(`SWR: dados alterados, cache e interface atualizados`, { collection: collectionName });
            if (onRevalidate) onRevalidate(freshData);
          } else {
            this.stats.swrDataUnchanged++;
            this.logger.debug(`SWR: revalidado sem alterações`, { collection: collectionName });
          }
        }

        this.trackOperation({
          operation: 'getDocsSWR',
          collection: collectionName,
          constraintCount: queryConstraints.length,
          origin,
          duration: Date.now() - startTime,
          docCount: freshData.length,
          cacheHit: false,
          swr: true,
          swrRevalidation: isBackground,
          dataChanged: hasChanged,
        });

        return freshData;
      } catch (error) {
        this.stats.errors++;
        this.logger.error(`SWR revalidation failed for ${collectionName}`, { error: error.message });
        throw error;
      }
    });

    const cachedEntry = this.cache.get(cacheKey);

    if (cachedEntry) {
      // Passo 1-2: cache existe → retorna IMEDIATAMENTE, independente do TTL
      this.stats.hits++;
      this.stats.swrInstantReturns++;

      this.trackOperation({
        operation: 'getDocsSWR',
        collection: collectionName,
        constraintCount: queryConstraints.length,
        origin,
        duration: Date.now() - startTime,
        docCount: cachedEntry.data.length,
        cacheHit: true,
        swr: true,
        instantReturn: true,
        savedReads: 1,
      });

      // Passo 3-4: revalida em segundo plano, sem bloquear o retorno acima
      revalidate(true).catch(() => {}); // erro já tratado/logado dentro de revalidate

      return cachedEntry.data;
    }

    // Sem cache disponível: não há dado para exibir de imediato,
    // aguarda a primeira consulta normalmente
    this.stats.misses++;
    return revalidate(false);
  }

  /**
   * Versão SWR de getDoc
   * @param collectionName - nome da coleção
   * @param documentId - ID do documento
   * @param onRevalidate - callback(freshData) chamado SOMENTE quando os dados mudarem
   * @returns Promise<data> - resolve imediatamente com o cache, se existir
   */
  async getDocSWR(collectionName, documentId, onRevalidate = null) {
    const startTime = Date.now();
    const cacheKey = this.getCacheKey(collectionName, 'getDoc', { id: documentId });
    const origin = this.getCallOrigin();

    const revalidate = (isBackground) => this.executeWithDedup(cacheKey, async () => {
      try {
        this.stats.queries++;

        const snapshot = await getDoc(doc(this.db, collectionName, documentId));
        const freshData = snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;

        const previousEntry = this.cache.get(cacheKey);
        const hasChanged = JSON.stringify(freshData) !== JSON.stringify(previousEntry ? previousEntry.data : undefined);

        this.setCacheEntry(cacheKey, freshData);

        if (isBackground) {
          this.stats.swrRevalidations++;

          if (hasChanged) {
            this.stats.swrDataChanged++;
            this.logger.info(`SWR: documento alterado, cache e interface atualizados`, { collection: collectionName, documentId });
            if (onRevalidate) onRevalidate(freshData);
          } else {
            this.stats.swrDataUnchanged++;
            this.logger.debug(`SWR: revalidado sem alterações`, { collection: collectionName, documentId });
          }
        }

        this.trackOperation({
          operation: 'getDocSWR',
          collection: collectionName,
          documentId,
          origin,
          duration: Date.now() - startTime,
          exists: !!freshData,
          docCount: freshData ? 1 : 0,
          cacheHit: false,
          swr: true,
          swrRevalidation: isBackground,
          dataChanged: hasChanged,
        });

        return freshData;
      } catch (error) {
        this.stats.errors++;
        this.logger.error(`SWR revalidation failed for ${collectionName}/${documentId}`, { error: error.message });
        throw error;
      }
    });

    const cachedEntry = this.cache.get(cacheKey);

    if (cachedEntry) {
      this.stats.hits++;
      this.stats.swrInstantReturns++;

      this.trackOperation({
        operation: 'getDocSWR',
        collection: collectionName,
        documentId,
        origin,
        duration: Date.now() - startTime,
        exists: !!cachedEntry.data,
        cacheHit: true,
        swr: true,
        instantReturn: true,
        savedReads: 1,
      });

      revalidate(true).catch(() => {});

      return cachedEntry.data;
    }

    this.stats.misses++;
    return revalidate(false);
  }

  /**
   * Adiciona um novo documento
   */
  async addDoc(collectionName, data) {
    try {
      this.stats.writes++;

      this.logger.debug(`Executing addDoc`, { collection: collectionName });

      // Adicionar timestamps automáticos
      const dataWithTimestamp = {
        ...data,
        createdAt: serverTimestamp(),
      };

      const docRef = await addDoc(collection(this.db, collectionName), dataWithTimestamp);

      // Invalidar cache da coleção
      this.invalidateCache(collectionName);

      this.logger.info(`addDoc completed`, {
        collection: collectionName,
        newDocId: docRef.id,
      });

      return docRef.id;
    } catch (error) {
      this.stats.errors++;
      this.logger.error(`addDoc failed for ${collectionName}`, { error: error.message });
      throw error;
    }
  }

  /**
   * Define um documento (cria ou sobrescreve)
   */
  async setDoc(collectionName, documentId, data, options = {}) {
    try {
      this.stats.writes++;

      this.logger.debug(`Executing setDoc`, { collection: collectionName, id: documentId });

      const dataWithTimestamp = {
        ...data,
        ...(options.merge ? {} : { createdAt: serverTimestamp() }),
      };

      await setDoc(doc(this.db, collectionName, documentId), dataWithTimestamp, options);

      // Invalidar cache
      this.invalidateCache(collectionName);

      this.logger.info(`setDoc completed`, {
        collection: collectionName,
        docId: documentId,
      });
    } catch (error) {
      this.stats.errors++;
      this.logger.error(`setDoc failed for ${collectionName}/${documentId}`, { error: error.message });
      throw error;
    }
  }

  /**
   * Atualiza um documento (merge)
   */
  async updateDoc(collectionName, documentId, updates) {
    try {
      this.stats.writes++;

      this.logger.debug(`Executing updateDoc`, { collection: collectionName, id: documentId });

      // Adicionar timestamp de atualização
      const updatesWithTimestamp = {
        ...updates,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(doc(this.db, collectionName, documentId), updatesWithTimestamp);

      // Invalidar cache
      this.invalidateCache(collectionName);

      this.logger.info(`updateDoc completed`, {
        collection: collectionName,
        docId: documentId,
      });
    } catch (error) {
      this.stats.errors++;
      this.logger.error(`updateDoc failed for ${collectionName}/${documentId}`, { error: error.message });
      throw error;
    }
  }

  /**
   * Deleta um documento
   */
  async deleteDoc(collectionName, documentId) {
    try {
      this.stats.writes++;

      this.logger.debug(`Executing deleteDoc`, { collection: collectionName, id: documentId });

      await deleteDoc(doc(this.db, collectionName, documentId));

      // Invalidar cache
      this.invalidateCache(collectionName);

      this.logger.info(`deleteDoc completed`, {
        collection: collectionName,
        docId: documentId,
      });
    } catch (error) {
      this.stats.errors++;
      this.logger.error(`deleteDoc failed for ${collectionName}/${documentId}`, { error: error.message });
      throw error;
    }
  }

  /**
   * Executa batch write
   */
  async batch(operations) {
    try {
      this.stats.writes++;

      this.logger.debug(`Executing batch with ${operations.length} operations`);

      const b = writeBatch(this.db);

      for (const op of operations) {
        const { type, collection: coll, documentId, data } = op;

        switch (type) {
          case 'set':
            b.set(doc(this.db, coll, documentId), data);
            break;
          case 'update':
            b.update(doc(this.db, coll, documentId), data);
            break;
          case 'delete':
            b.delete(doc(this.db, coll, documentId));
            break;
          default:
            throw new Error(`Unknown batch operation: ${type}`);
        }
      }

      await b.commit();

      // Invalidar caches de todas as coleções envolvidas
      const collections = [...new Set(operations.map(op => op.collection))];
      collections.forEach(coll => this.invalidateCache(coll));

      this.logger.info(`batch completed`, {
        operationsCount: operations.length,
        collectionsAffected: collections.length,
      });
    } catch (error) {
      this.stats.errors++;
      this.logger.error(`batch failed`, { error: error.message });
      throw error;
    }
  }

  /**
   * Executa transaction
   *
   * O callback recebe um adaptador `t` que expõe a mesma API do
   * FirestoreManager (coleção + id), em vez do objeto de transação nativo
   * do Firestore. Assim as telas escrevem `t.getDoc("counters","rits")`,
   * `t.setDoc(...)`, `t.addDoc(...)` etc., sem lidar com refs/snapshots.
   *
   * Regra do Firestore: todas as leituras (getDoc) devem ocorrer antes de
   * qualquer escrita (set/update/delete) dentro da mesma transação.
   */
  async transaction(updateFn) {
    const queueKey = "global_transaction";

    // Aguarda qualquer transação anterior completar (fila serializada)
    if (this.transactionQueues.has(queueKey)) {
      await this.transactionQueues.get(queueKey);
    }

    try {
      this.stats.writes++;

      this.logger.debug(`Executing transaction`);

      // Cria uma Promise para essa transação e armazena na fila
      const transactionPromise = runTransaction(this.db, async (tx) => {
        const adapter = {
          // Leitura: retorna { id, ...dados } ou null (mesma forma de getDoc)
          getDoc: async (collectionName, documentId) => {
            const snap = await tx.get(doc(this.db, collectionName, documentId));
            return snap.exists() ? { id: snap.id, ...snap.data() } : null;
          },
          // Cria/sobrescreve o documento (equivalente a t.set nativo)
          setDoc: (collectionName, documentId, data) => {
            tx.set(doc(this.db, collectionName, documentId), data);
          },
          // Atualiza campos de um documento existente
          updateDoc: (collectionName, documentId, data) => {
            tx.update(doc(this.db, collectionName, documentId), data);
          },
          // Remove o documento
          deleteDoc: (collectionName, documentId) => {
            tx.delete(doc(this.db, collectionName, documentId));
          },
          // Cria documento com id automático; retorna o id gerado
          addDoc: (collectionName, data) => {
            const ref = doc(collection(this.db, collectionName));
            tx.set(ref, data);
            return ref.id;
          },
        };
        return updateFn(adapter);
      });

      this.transactionQueues.set(queueKey, transactionPromise);
      const result = await transactionPromise;

      // Remove da fila após conclusão
      this.transactionQueues.delete(queueKey);

      // Nota: A transação pode afetar múltiplas coleções
      // Invalidar cache geral como precaução
      this.invalidateCache();

      this.logger.info(`transaction completed`);

      return result;
    } catch (error) {
      this.stats.errors++;
      this.logger.error(`transaction failed`, { error: error.message });
      this.transactionQueues.delete(queueKey);
      throw error;
    }
  }

  /**
   * Listener real-time com onSnapshot
   * Retorna função de unsubscribe
   */
  onSnapshot(listenerId, collectionName, queryConstraints = [], onNext, onError) {
    try {
      this.logger.debug(`Setting up listener`, { collection: collectionName, listenerId });

      const q = query(collection(this.db, collectionName), ...queryConstraints);

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const data = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          }));

          this.logger.debug(`Listener update`, {
            collection: collectionName,
            docsCount: data.length,
            listenerId,
          });

          onNext(data);
        },
        (error) => {
          this.stats.errors++;
          this.logger.error(`Listener error for ${collectionName}`, { error: error.message, listenerId });
          if (onError) onError(error);
        }
      );

      // Armazenar listener para cleanup posterior
      this.listeners.set(listenerId, unsubscribe);

      this.logger.info(`Listener created`, { listenerId, collection: collectionName });

      return unsubscribe;
    } catch (error) {
      this.stats.errors++;
      this.logger.error(`onSnapshot setup failed for ${collectionName}`, { error: error.message });
      throw error;
    }
  }

  /**
   * Remove um listener específico
   */
  removeListener(listenerId) {
    try {
      const unsubscribe = this.listeners.get(listenerId);

      if (unsubscribe) {
        unsubscribe();
        this.listeners.delete(listenerId);
        this.logger.info(`Listener removed`, { listenerId });
      } else {
        this.logger.warn(`Listener not found`, { listenerId });
      }
    } catch (error) {
      this.logger.error(`Failed to remove listener`, { error: error.message, listenerId });
    }
  }

  /**
   * Remove todos os listeners
   */
  removeAllListeners() {
    try {
      this.logger.info(`Removing all listeners`, { count: this.listeners.size });

      for (const [listenerId, unsubscribe] of this.listeners) {
        unsubscribe();
      }

      this.listeners.clear();
      this.logger.info(`All listeners removed`);
    } catch (error) {
      this.logger.error(`Failed to remove all listeners`, { error: error.message });
    }
  }

  /**
   * Retorna estatísticas detalhadas de cache e operações
   */
  getStats() {
    const totalQueries = this.stats.hits + this.stats.misses;
    const hitRate = totalQueries > 0 ? ((this.stats.hits / totalQueries) * 100).toFixed(2) : 0;
    const missRate = totalQueries > 0 ? ((this.stats.misses / totalQueries) * 100).toFixed(2) : 0;

    // Calcular tamanho total do cache em bytes
    let totalCacheSize = 0;
    const cacheByCollection = {};

    for (const [key, entry] of this.cache) {
      const collection = key.split(':')[0];
      const size = entry.size || 0;
      totalCacheSize += size;

      if (!cacheByCollection[collection]) {
        cacheByCollection[collection] = { count: 0, size: 0, ttl: COLLECTION_TTL_CONFIG[collection] };
      }
      cacheByCollection[collection].count++;
      cacheByCollection[collection].size += size;
    }

    // Calcular economias
    const { dedupSavings, cacheSavings, totalSavings } = this.calculateSavedReads();
    const totalRequests = this.stats.queries + this.stats.dedupedRequests;
    const dedupRate = totalRequests > 0 ? ((this.stats.dedupedRequests / totalRequests) * 100).toFixed(2) : 0;

    return {
      cache: {
        size: this.cache.size,
        totalBytes: totalCacheSize,
        byCollection: cacheByCollection,
      },
      listeners: {
        count: this.listeners.size,
        ids: Array.from(this.listeners.keys()),
      },
      stats: {
        queries: this.stats.queries,
        writes: this.stats.writes,
        errors: this.stats.errors,
        hits: this.stats.hits,
        misses: this.stats.misses,
        observedDocs: this.stats.observedDocs,
        dedupedRequests: this.stats.dedupedRequests,
        hitRate: `${hitRate}%`,
        missRate: `${missRate}%`,
        dedupRate: `${dedupRate}%`,
        totalOperations: this.stats.queries + this.stats.writes,
        savedReads: totalSavings,
        savedReadsByDedup: dedupSavings,
        savedReadsByCache: cacheSavings,
        swrInstantReturns: this.stats.swrInstantReturns,
        swrRevalidations: this.stats.swrRevalidations,
        swrDataChanged: this.stats.swrDataChanged,
        swrDataUnchanged: this.stats.swrDataUnchanged,
      },
      ttlConfig: COLLECTION_TTL_CONFIG,
    };
  }

  /**
   * Retorna métricas detalhadas incluindo histórico de operações
   */
  getDetailedMetrics() {
    const stats = this.getStats();
    const { dedupSavings, cacheSavings, totalSavings } = this.calculateSavedReads();
    const totalRequests = this.stats.queries + this.stats.dedupedRequests;
    const dedupRate = totalRequests > 0 ? (this.stats.dedupedRequests / totalRequests) * 100 : 0;
    const overallReduction = totalRequests > 0 ? (totalSavings / (totalRequests + totalSavings)) * 100 : 0;

    // Operações por coleção
    const operationsByCollection = {};
    for (const op of this.operationHistory) {
      if (!operationsByCollection[op.collection]) {
        operationsByCollection[op.collection] = {
          count: 0,
          avgDuration: 0,
          errors: 0,
          savedReads: 0,
          lastOperation: null,
        };
      }
      const col = operationsByCollection[op.collection];
      col.count++;
      col.avgDuration = (col.avgDuration * (col.count - 1) + (op.duration || 0)) / col.count;
      if (op.error) col.errors++;
      col.savedReads += op.savedReads || 0;
      col.lastOperation = op.timestamp;
    }

    // Operações por origem (arquivo/linha)
    const operationsByOrigin = {};
    for (const op of this.operationHistory) {
      if (!operationsByOrigin[op.origin]) {
        operationsByOrigin[op.origin] = {
          count: 0,
          lastTime: null,
          collections: new Set(),
        };
      }
      operationsByOrigin[op.origin].count++;
      operationsByOrigin[op.origin].lastTime = op.timestamp;
      operationsByOrigin[op.origin].collections.add(op.collection);
    }

    // Converter Sets para arrays
    Object.keys(operationsByOrigin).forEach(origin => {
      operationsByOrigin[origin].collections = Array.from(operationsByOrigin[origin].collections);
    });

    return {
      summary: {
        totalRequests,
        totalOperations: this.stats.queries + this.stats.writes,
        dedupRate: `${dedupRate.toFixed(2)}%`,
        overallReduction: `${overallReduction.toFixed(2)}%`,
        totalSavedReads: totalSavings,
        dedupSavings,
        cacheSavings,
      },
      recentOperations: this.operationHistory.slice(-20),
      operationsByCollection,
      operationsByOrigin,
      stats: stats.stats,
      cache: stats.cache,
      listeners: stats.listeners,
    };
  }

  /**
   * Persistência cross-sessão de métricas ─────────────────────────────────────
   * Salva um snapshot cumulativo dos contadores desta sessão em user_analytics
   * (com action="firestore_metrics_snapshot") e reseta os contadores locais.
   * Delta-based: cada doc representa "atividade entre windowStart e agora"
   * daquela sessão do navegador — agregar é só somar campos numéricos.
   * Reusa a coleção user_analytics para NÃO precisar alterar firestore.rules.
   *
   * O tipo="metrics" (diferente de "read"/"write") faz esses docs serem
   * filtrados fora das abas Leituras/Escritas existentes (que filtram por
   * type === abaAtiva), então não poluem a visualização.
   */
  async saveMetricsSnapshot(userInfo) {
    if (!userInfo?.uid) return; // sem usuário logado, não faz sentido salvar
    if (this._snapshotInFlight) return this._snapshotInFlight;
    const metrics = this.getDetailedMetrics();
    const historyLength = this.operationHistory.length;
    // O histórico de detalhe é limitado a 500 entradas; os contadores não são.
    if (metrics.stats.totalOperations === 0) return;

    const windowStart = this._persistWindowStart || new Date().toISOString();
    const windowEnd = new Date().toISOString();
    const sessionId = this._sessionId || (this._sessionId = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`);

    // Compacta operationsByOrigin/Collection: só campos que a UI de análise
    // usa. Evita salvar avgDuration/lastOperation (não úteis pra agregar).
    const byCollection = {};
    Object.entries(metrics.operationsByCollection).forEach(([col, d]) => {
      byCollection[col] = { count: d.count, errors: d.errors, savedReads: d.savedReads };
    });
    const byOrigin = {};
    Object.entries(metrics.operationsByOrigin).forEach(([origin, d]) => {
      byOrigin[origin] = { count: d.count, collections: d.collections };
    });

    this._snapshotInFlight = (async () => {
    try {
      await this.addDoc('user_analytics', {
        action: 'firestore_metrics_snapshot',
        type: 'metrics', // filtrado fora das abas Leituras/Escritas
        userId: userInfo.uid,
        userName: userInfo.name || userInfo.userName || 'Desconhecido',
        userRole: userInfo.role || 'unknown',
        userUnit: userInfo.unitCode || 'N/A',
        sessionId,
        windowStart,
        windowEnd,
        summary: {
          totalOperations: metrics.stats.totalOperations,
          totalRequests: metrics.summary.totalRequests,
          totalSavedReads: metrics.summary.totalSavedReads,
        },
        stats: {
          hits: metrics.stats.hits,
          misses: metrics.stats.misses,
          observedDocs: metrics.stats.observedDocs,
          queries: metrics.stats.queries,
          writes: metrics.stats.writes,
          errors: metrics.stats.errors,
          dedupedRequests: metrics.stats.dedupedRequests,
        },
        byCollection,
        byOrigin,
        timestamp: new Date(), // usar cliente Date; addDoc já adiciona createdAt
      });
      // Após salvar, reseta contadores para começar nova janela — evita que o
      // próximo snapshot re-conte as mesmas operações (delta-based).
      this.operationHistory.splice(0, Math.min(historyLength, this.operationHistory.length));
      for (const key of ['hits', 'misses', 'queries', 'errors', 'dedupedRequests', 'observedDocs', 'swrInstantReturns', 'swrRevalidations', 'swrDataChanged', 'swrDataUnchanged']) {
        this.stats[key] = Math.max(0, this.stats[key] - (metrics.stats[key] || 0));
      }
      this.stats.writes = Math.max(0, this.stats.writes - metrics.stats.writes - 1);
      this._persistWindowStart = new Date().toISOString();
    } catch (e) {
      this.logger.warn('saveMetricsSnapshot falhou (não crítico)', { error: e.message });
    }
    })();
    try { return await this._snapshotInFlight; } finally { this._snapshotInFlight = null; }
  }

  /**
   * Liga a persistência automática de métricas.
   * @param userInfo - { uid, name, role, unitCode } do usuário logado
   * @param opts.intervalMs - intervalo entre snapshots (default 15 min)
   * @returns função de cleanup (chame no logout)
   */
  startAutoPersist(userInfo, { intervalMs = 15 * 60 * 1000 } = {}) {
    // Idempotente: se já estava rodando (ex.: hot-reload), para o anterior
    this.stopAutoPersist();
    if (!userInfo?.uid) return () => {};
    this._persistWindowStart = new Date().toISOString();

    // Snapshot periódico
    this._persistInterval = setInterval(() => {
      this.saveMetricsSnapshot(userInfo);
    }, intervalMs);

    // Flush ao fechar/mudar de aba (best-effort; o navegador pode cortar
    // requisições em beforeunload, mas visibilitychange costuma funcionar)
    this._persistUnloadHandler = () => { this.saveMetricsSnapshot(userInfo); };
    window.addEventListener('visibilitychange', this._persistUnloadHandler);
    window.addEventListener('pagehide', this._persistUnloadHandler);

    return () => this.stopAutoPersist();
  }

  stopAutoPersist() {
    if (this._persistInterval) {
      clearInterval(this._persistInterval);
      this._persistInterval = null;
    }
    if (this._persistUnloadHandler) {
      window.removeEventListener('visibilitychange', this._persistUnloadHandler);
      window.removeEventListener('pagehide', this._persistUnloadHandler);
      this._persistUnloadHandler = null;
    }
  }

  /**
   * Inicia limpeza automática de cache (executada periodicamente)
   * @param intervalMs - Intervalo em ms para executar limpeza (default: 5 min)
   */
  startAutoCleaner(intervalMs = 5 * 60 * 1000) {
    if (this.autoCleanerInterval) {
      this.logger.warn('AutoCleaner already running');
      return;
    }

    this.autoCleanerInterval = setInterval(() => {
      const result = this.cleanExpiredCaches();
      if (result.deleted > 0) {
        this.logger.debug('AutoCleaner executed', result);
      }
    }, intervalMs);

    this.logger.info('AutoCleaner started', { intervalMs });
  }

  /**
   * Para a limpeza automática de cache
   */
  stopAutoCleaner() {
    if (this.autoCleanerInterval) {
      clearInterval(this.autoCleanerInterval);
      this.autoCleanerInterval = null;
      this.logger.info('AutoCleaner stopped');
    }
  }

  /**
   * Limpa toda a cache e listeners
   */
  clearAll() {
    try {
      this.logger.info(`Clearing all cache and listeners`);
      this.cache.clear();
      this.removeAllListeners();
      this.logger.info(`Cleared successfully`);
    } catch (error) {
      this.logger.error(`Failed to clear all`, { error: error.message });
    }
  }

  /**
   * Re-exporta utilitários do Firestore para uso direto
   * (útil para queries complexas que precisam de constructores específicos)
   */
  getFirestoreUtils() {
    return {
      collection,
      query,
      where,
      orderBy,
      limit,
      startAfter,
      doc,
      serverTimestamp,
      increment,
    };
  }
}

export default FirestoreManager;
